import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Shield, Plus, Trash2, Search, Pencil } from "lucide-react";

interface TrafficControl {
  id: number;
  name: string;
  status: string;
  controlType: string;
  targetingAction: string;
  variable: string;
  comparisonMethod: string;
  values: string;
  isApplyAllOffers: boolean;
  offerIds: string;
  affiliateIds: string;
  dateValidFrom: string | null;
  dateValidTo: string | null;
  createdAt: string;
}

const variables = ["sub1", "sub2", "sub3", "sub4", "sub5", "source_id", "referrer", "isp", "ip_address"];
const comparisonMethods = ["exact", "contains", "starts_with", "ends_with", "regex"];

export function TrafficControlsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["traffic-controls", { search }],
    queryFn: async () => {
      const qp: Record<string, string> = { pageSize: "100" };
      if (search) qp.search = search;
      const res = await api.get("/v1/traffic-controls", qp);
      if (!res.ok) throw new Error("Failed to fetch traffic controls");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete(`/v1/traffic-controls/${id}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["traffic-controls"] }),
  });

  const controls: TrafficControl[] = data?.data ?? [];

  function handleEdit(tc: TrafficControl) {
    setEditingId(tc.id);
    setShowForm(true);
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader title="Traffic Controls" description="Whitelist or blacklist traffic based on variables" />
        <button onClick={() => { setEditingId(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search rules..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : controls.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No traffic control rules yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Type</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Action</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Variable</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {controls.map((tc) => (
                <tr key={tc.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{tc.name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${tc.controlType === "whitelist" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                      {tc.controlType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 capitalize">{tc.targetingAction}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">{tc.variable}</td>
                  <td className="px-4 py-3"><StatusBadge status={tc.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(tc)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
                        <Pencil className="w-4 h-4" /></button>
                      <button onClick={() => { if (window.confirm(`Delete "${tc.name}"?`)) deleteMutation.mutate(tc.id); }}
                        className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TrafficControlFormModal
          editingId={editingId}
          controls={controls}
          onClose={() => { setShowForm(false); setEditingId(null); }}
        />
      )}
    </div>
  );
}

function TrafficControlFormModal({ editingId, controls, onClose }: {
  editingId: number | null;
  controls: TrafficControl[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const existing = editingId ? controls.find((c) => c.id === editingId) : undefined;
  const isEdit = !!existing;

  const [name, setName] = useState(existing?.name ?? "");
  const [controlType, setControlType] = useState(existing?.controlType ?? "blacklist");
  const [targetingAction, setTargetingAction] = useState(existing?.targetingAction ?? "block");
  const [variable, setVariable] = useState(existing?.variable ?? "sub1");
  const [comparisonMethod, setComparisonMethod] = useState(existing?.comparisonMethod ?? "exact");
  const [values, setValues] = useState(existing?.values ?? "");
  const [status, setStatus] = useState(existing?.status ?? "active");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { name, controlType, targetingAction, variable, comparisonMethod, values, status };
      const res = isEdit
        ? await api.patch(`/v1/traffic-controls/${editingId}`, payload)
        : await api.post("/v1/traffic-controls", payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["traffic-controls"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{isEdit ? "Edit" : "Create"} Traffic Control</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="e.g., Block bot traffic" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Control Type</label>
              <select value={controlType} onChange={(e) => setControlType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="blacklist">Blacklist</option>
                <option value="whitelist">Whitelist</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select value={targetingAction} onChange={(e) => setTargetingAction(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="block">Block</option>
                <option value="fail_traffic">Fail Traffic</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Variable</label>
              <select value={variable} onChange={(e) => setVariable(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
                {variables.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Comparison</label>
              <select value={comparisonMethod} onChange={(e) => setComparisonMethod(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
                {comparisonMethods.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Values (one per line)</label>
            <textarea value={values} onChange={(e) => setValues(e.target.value)} rows={4}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder={"value1\nvalue2\nvalue3"} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Create Rule"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
